import React, { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { clientApi } from '../api/client'
import { userApi } from '../api/user'
import { validationUtils } from '../utils'
import { ClientErrorView } from '../components/ClientErrorView'
import { PasswordCriteriaList } from '../components/PasswordCriteriaList'
import type { Client, ClientLoadErrorType, SignupData } from '../types'

const CGU_URL = 'https://go.olecio.fr/cgu/'
const PRIVACY_URL = 'https://go.olecio.fr/mentions-legales/'

export const SignupView: React.FC = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const clientId = searchParams.get('client_id')
  
  const [client, setClient] = useState<Client | null>(null)
  const [formData, setFormData] = useState<SignupData>({
    firstName: '',
    lastName: '',
    phoneNumber: '',
    email: '',
    password: '',
    confirmPassword: ''
  })
  const [errors, setErrors] = useState<Partial<SignupData>>({})
  const [clientLoadError, setClientLoadError] = useState<ClientLoadErrorType | null>(null)
  const [isPasswordFocused, setIsPasswordFocused] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingClient, setIsLoadingClient] = useState(true)

  const passwordCriteria = validationUtils.getPasswordCriteria(formData.password)
  const showPasswordCriteria = isPasswordFocused || formData.password.length > 0

  // Charger les informations du client
  useEffect(() => {
    const loadClient = async () => {
      if (!clientId) {
        setClientLoadError('missing_client_id')
        setIsLoadingClient(false)
        return
      }

      try {
        const clientData = await clientApi.getClientByPublicId(clientId)
        setClient(clientData)
      } catch (error) {
        console.error('Error loading client:', error)
        setClientLoadError('client_not_found')
      } finally {
        setIsLoadingClient(false)
      }
    }

    loadClient()
  }, [clientId])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    
    // Effacer l'erreur du champ modifié
    if (errors[name as keyof SignupData]) {
      setErrors(prev => ({ ...prev, [name]: undefined }))
    }
  }

  const validateForm = (): boolean => {
    const newErrors: Partial<SignupData> = {}

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'Le prénom est requis'
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Le nom est requis'
    }

    if (!formData.email) {
      newErrors.email = 'L\'email est requis'
    } else if (!validationUtils.isValidEmail(formData.email)) {
      newErrors.email = 'Format d\'email invalide'
    }

    if (!formData.password) {
      newErrors.password = 'Le mot de passe est requis'
    } else if (!validationUtils.isValidPassword(formData.password)) {
      newErrors.password = 'Le mot de passe ne respecte pas toutes les exigences'
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'La confirmation du mot de passe est requise'
    } else if (!validationUtils.passwordsMatch(formData.password, formData.confirmPassword)) {
      newErrors.confirmPassword = 'Les mots de passe ne correspondent pas'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm() || !clientId) return

    setIsLoading(true)
    try {
      await userApi.signup(formData)
      
      // Rediriger vers la page OTP avec les credentials
      navigate(`/otp?client_id=${clientId}`, {
        state: { 
          email: formData.email, 
          password: formData.password 
        }
      })
    } catch (error: any) {
      console.error('Signup error:', error)
      setErrors({ 
        email: error.response?.data?.message || 'Erreur lors de la création du compte' 
      })
    } finally {
      setIsLoading(false)
    }
  }

  if (isLoadingClient) {
    return (
      <div className="card fade-in">
        <div className="card-header">
          <div className="spinner"></div>
          <p>Chargement...</p>
        </div>
      </div>
    )
  }

  if (clientLoadError) {
    return <ClientErrorView type={clientLoadError} clientId={clientId} />
  }

  if (!client) {
    return null
  }

  return (
    <div className="card fade-in">
      <div className="card-header">
        {client.logo && (
          <img 
            src={client.logo} 
            alt={client.title} 
            style={{ height: '60px', marginBottom: '1rem' }}
          />
        )}
        <h1 className="card-title">Créer un compte partagé</h1>
        <p className="card-subtitle">Rejoignez {client.title} et les autres services proposés par Olecio</p>
      </div>

      <form onSubmit={handleSubmit}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label htmlFor="firstName" className="form-label">
              Prénom<span className="form-label-required" aria-hidden="true">*</span>
            </label>
            <input
              type="text"
              id="firstName"
              name="firstName"
              className={`form-input ${errors.firstName ? 'shake' : ''}`}
              value={formData.firstName}
              onChange={handleInputChange}
              placeholder="Votre prénom"
              disabled={isLoading}
              required
            />
            {errors.firstName && (
              <div className="form-error">{errors.firstName}</div>
            )}
          </div>

          <div className="form-group" style={{ marginBottom: 0 }}>
            <label htmlFor="lastName" className="form-label">
              Nom<span className="form-label-required" aria-hidden="true">*</span>
            </label>
            <input
              type="text"
              id="lastName"
              name="lastName"
              className={`form-input ${errors.lastName ? 'shake' : ''}`}
              value={formData.lastName}
              onChange={handleInputChange}
              placeholder="Votre nom"
              disabled={isLoading}
              required
            />
            {errors.lastName && (
              <div className="form-error">{errors.lastName}</div>
            )}
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="phoneNumber" className="form-label">
            Numéro de téléphone
            <span className="form-label-optional"> (optionnel)</span>
          </label>
          <input
            type="tel"
            id="phoneNumber"
            name="phoneNumber"
            className="form-input"
            value={formData.phoneNumber}
            onChange={handleInputChange}
            placeholder="+33 6 12 34 56 78"
            disabled={isLoading}
          />
        </div>

        <div className="form-group">
          <label htmlFor="email" className="form-label">
            Adresse email<span className="form-label-required" aria-hidden="true">*</span>
          </label>
          <input
            type="email"
            id="email"
            name="email"
            className={`form-input ${errors.email ? 'shake' : ''}`}
            value={formData.email}
            onChange={handleInputChange}
            placeholder="votre@email.com"
            disabled={isLoading}
            required
          />
          {errors.email && (
            <div className="form-error">{errors.email}</div>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="password" className="form-label">
            Mot de passe<span className="form-label-required" aria-hidden="true">*</span>
          </label>
          <input
            type="password"
            id="password"
            name="password"
            className={`form-input ${errors.password ? 'shake' : ''}`}
            value={formData.password}
            onChange={handleInputChange}
            onFocus={() => setIsPasswordFocused(true)}
            onBlur={() => setIsPasswordFocused(false)}
            placeholder="Votre mot de passe"
            disabled={isLoading}
            required
            aria-describedby={showPasswordCriteria ? 'password-criteria' : undefined}
          />
          {showPasswordCriteria && (
            <div id="password-criteria">
              <PasswordCriteriaList criteria={passwordCriteria} />
            </div>
          )}
          {errors.password && (
            <div className="form-error">{errors.password}</div>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="confirmPassword" className="form-label">
            Confirmer le mot de passe<span className="form-label-required" aria-hidden="true">*</span>
          </label>
          <input
            type="password"
            id="confirmPassword"
            name="confirmPassword"
            className={`form-input ${errors.confirmPassword ? 'shake' : ''}`}
            value={formData.confirmPassword}
            onChange={handleInputChange}
            placeholder="Répétez votre mot de passe"
            disabled={isLoading}
            required
          />
          {errors.confirmPassword && (
            <div className="form-error">{errors.confirmPassword}</div>
          )}
        </div>

        <p className="signup-cgu-notice">
          En cliquant sur « Créer mon compte », vous acceptez les{' '}
          <a
            href={CGU_URL}
            className="link"
            target="_blank"
            rel="noopener noreferrer"
          >
            Conditions générales d&apos;utilisation
          </a>{' '}et confirmez avoir lu la{' '}
          <a
            href={PRIVACY_URL}
            className="link"
            target="_blank"
            rel="noopener noreferrer"
          >
            Politique de confidentialité
          </a>.
        </p>

        <button
          type="submit"
          className="btn btn-primary btn-full"
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <div className="spinner"></div>
              Création...
            </>
          ) : (
            'Créer mon compte'
          )}
        </button>
      </form>

      <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
        <p style={{ color: 'var(--gray-600)', fontSize: '0.875rem' }}>
          Déjà un compte ?{' '}
          <a 
            href={`/login?client_id=${clientId}`}
            className="link"
          >
            Se connecter
          </a>
        </p>
      </div>
    </div>
  )
}
